import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Textarea } from '@/components/ui/Textarea'
import { useForm } from '@inertiajs/react'
import { FormEvent } from 'react'

interface Community {
  id: number
  name: string
}

interface NewProps {
  communities: Community[]
}

export default function New({ communities }: NewProps) {
  const { data, setData, post, processing, errors } = useForm({
    title: '',
    description: '',
    theme: '',
    community_id: '',
    submission_start_date: '',
    submission_end_date: '',
    voting_end_date: '',
    participation_points: 30,
    vote_points: 2,
    first_place_points: 50,
    second_place_points: 30,
    third_place_points: 20
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post('/admin/photo_contests')
  }

  return (
    <AdminLayout
      title="Nouveau Concours Photo"
      breadcrumbs={[
        { label: 'Concours Photo', href: '/admin/photo_contests' },
        { label: 'Nouveau' }
      ]}
    >
      <div className="max-w-3xl">
        <form onSubmit={handleSubmit}>
          <Card className="p-6">
            <div className="space-y-6">
              {/* Titre */}
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  Titre *
                </label>
                <Input
                  value={data.title}
                  onChange={(e) => setData('title', e.target.value)}
                  placeholder="Ex: Challenge Corner - Février 2026"
                  required
                />
                {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
              </div>

              {/* Thème */}
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">Thème</label>
                <Input
                  value={data.theme}
                  onChange={(e) => setData('theme', e.target.value)}
                  placeholder="Ex: Décoration Saint-Valentin"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">Description</label>
                <Textarea
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                  placeholder="Instructions pour les participants..."
                  rows={4}
                />
              </div>

              {/* Communauté */}
              <div>
                <label className="block text-sm font-medium text-text-dark mb-1">
                  Communauté *
                </label>
                <Select
                  value={data.community_id}
                  onChange={(e) => setData('community_id', e.target.value)}
                  required
                >
                  <option value="">Sélectionner</option>
                  {communities.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-dark mb-1">
                    Début soumissions *
                  </label>
                  <Input
                    type="datetime-local"
                    value={data.submission_start_date}
                    onChange={(e) => setData('submission_start_date', e.target.value)}
                    required
                  />
                  {errors.submission_start_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.submission_start_date}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-dark mb-1">
                    Fin soumissions *
                  </label>
                  <Input
                    type="datetime-local"
                    value={data.submission_end_date}
                    onChange={(e) => setData('submission_end_date', e.target.value)}
                    required
                  />
                  {errors.submission_end_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.submission_end_date}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-dark mb-1">
                    Fin votes *
                  </label>
                  <Input
                    type="datetime-local"
                    value={data.voting_end_date}
                    onChange={(e) => setData('voting_end_date', e.target.value)}
                    required
                  />
                  {errors.voting_end_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.voting_end_date}</p>
                  )}
                  <p className="mt-1 text-xs text-text-gray">
                    Doit être après la fin des soumissions
                  </p>
                </div>
              </div>

              {/* Points */}
              <div>
              <h3 className="text-sm font-semibold text-text-dark mb-3">Nombre de points attribués</h3>
              <div className="grid grid-cols-5 gap-3">
                <div>
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    Participation
                  </label>
                  <Input
                    type="number"
                    value={data.participation_points}
                    onChange={(e) => setData('participation_points', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    Par vote
                  </label>
                  <Input
                    type="number"
                    value={data.vote_points}
                    onChange={(e) => setData('vote_points', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    1ère place
                  </label>
                  <Input
                    type="number"
                    value={data.first_place_points}
                    onChange={(e) => setData('first_place_points', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    2ème place
                  </label>
                  <Input
                    type="number"
                    value={data.second_place_points}
                    onChange={(e) => setData('second_place_points', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-dark mb-1">
                    3ème place
                  </label>
                  <Input
                    type="number"
                    value={data.third_place_points}
                    onChange={(e) => setData('third_place_points', parseInt(e.target.value))}
                    min={1}
                  />
                </div>
              </div>
              </div>
            </div>

            {/* Submit */}
            <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-4">
              <Button type="button" variant="secondary" onClick={() => history.back()}>
                Annuler
              </Button>
              <Button type="submit" disabled={processing}>
                {processing ? 'Création...' : 'Créer le concours'}
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </AdminLayout>
  )
}
